import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useEntities } from '../../hooks/useSWR';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';

const STATUS_ORDER = ['Active', 'Expiring Soon', 'Expired', 'Renewed'];

const contractStatusSemantic = (status) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Expiring Soon':
      return 'warning';
    case 'Expired':
      return 'danger';
    case 'Renewed':
      return 'info';
    default:
      return 'neutral';
  }
};

const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Active':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'Expiring Soon':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2 2m6-2a8 8 0 11-16 0 8 8 0 0116 0z" />
        </svg>
      );
    case 'Expired':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01m-9 4h18c1.1 0 1.8-1.2 1.2-2.1l-9-14a1.4 1.4 0 00-2.4 0l-9 14A1.4 1.4 0 003 20z" />
        </svg>
      );
    case 'Renewed':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.6M20 20v-5h-.6M5.6 9A8 8 0 0119 6m-.6 9A8 8 0 015 18" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
        </svg>
      );
  }
};

const ContractDashboardPage = () => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  const navigate = useNavigate();
  const { data: contracts = [], isLoading, isError } = useEntities('/contract');

  const scopedContracts = contracts;

  const analytics = useMemo(() => {
    const statusCounts = {
      Active: 0,
      'Expiring Soon': 0,
      Expired: 0,
      Renewed: 0,
    };

    const expiryBuckets = {
      'Next 30 days': 0,
      'Next 60 days': 0,
      'Next 90 days': 0,
      Expired: 0,
    };

    const supplierRiskMap = new Map();
    const actionable = [];

    scopedContracts.forEach((contract) => {
      const status = STATUS_ORDER.includes(contract.contractStatus)
        ? contract.contractStatus
        : 'Active';
      statusCounts[status] += 1;

      const daysLeft = getDaysUntilExpiry(contract.expiryDate);
      if (daysLeft !== null) {
        if (daysLeft < 0) {
          expiryBuckets.Expired += 1;
        } else if (daysLeft <= 30) {
          expiryBuckets['Next 30 days'] += 1;
        } else if (daysLeft <= 60) {
          expiryBuckets['Next 60 days'] += 1;
        } else if (daysLeft <= 90) {
          expiryBuckets['Next 90 days'] += 1;
        }
      }

      if (status === 'Expired' || status === 'Expiring Soon') {
        actionable.push({
          id: contract.id,
          contractNumber: contract.contractNumber,
          contractTitle: contract.contractTitle,
          supplierName: contract?.supplier?.name || 'Unknown entity',
          categoryName: contract?.category?.catName || 'Unknown Category',
          expiryDate: contract.expiryDate,
          daysLeft,
          status,
        });

        const supplierName = contract?.supplier?.name || 'Unknown entity';
        supplierRiskMap.set(supplierName, (supplierRiskMap.get(supplierName) || 0) + 1);
      }
    });

    const topRiskSuppliers = Array.from(supplierRiskMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const actionableSorted = actionable.sort((a, b) => {
      if (a.daysLeft === null) return 1;
      if (b.daysLeft === null) return -1;
      return a.daysLeft - b.daysLeft;
    });

    return {
      totalContracts: scopedContracts.length,
      statusCounts,
      expiryBuckets,
      topRiskSuppliers,
      actionable: actionableSorted,
    };
  }, [scopedContracts]);

  if (isError) {
    return (
      <div className={cn('p-6 min-h-screen', dash.pageBg())}>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Failed to load contract analytics.
        </div>
      </div>
    );
  }

  return (
    <div className={cn('p-6 min-h-screen space-y-6', dash.pageBg())}>
      <div className={dash.widgetShell('rounded-2xl')}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className={cn('inline-flex items-center gap-2 mb-2', dash.pill('info'))}>
              <span className={cn('w-2 h-2 rounded-full', dash.dot('info'))} />
              Contract Intelligence
            </div>
            <h1 className={`text-2xl font-bold ${theme.text.primary}`}>Contract Dashboard</h1>
            <p className={`text-sm ${theme.text.secondary}`}>
              Lifecycle and expiry insights based on automated status calculation.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/contract')}
              className={dash.btnPrimary('px-4 py-2')}
            >
              Open Contracts
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className={dash.metricCard('rounded-xl')}>
          <p className={`text-xs uppercase tracking-wide ${theme.text.secondary}`}>Total</p>
          <p className={`text-3xl font-bold ${theme.text.primary}`}>{analytics.totalContracts}</p>
          <p className={`text-xs mt-1 ${theme.text.secondary}`}>All supplier contracts</p>
        </div>
        {STATUS_ORDER.map((status) => {
          const count = analytics.statusCounts[status];
          const pct = analytics.totalContracts > 0
            ? Math.round((count / analytics.totalContracts) * 100)
            : 0;
          return (
            <div key={status} className={dash.metricCard('rounded-xl')}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-xs uppercase tracking-wide ${theme.text.secondary}`}>{status}</p>
                  <p className={`text-3xl font-bold ${theme.text.primary}`}>{count}</p>
                  <p className={`text-xs mt-1 ${theme.text.secondary}`}>{pct}% of contracts</p>
                </div>
                <div className={cn('p-2 rounded-lg', dash.iconBg(contractStatusSemantic(status)), dash.text(contractStatusSemantic(status)))}>
                  {getStatusIcon(status)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className={dash.widgetShell()}>
          <h2 className={`text-base font-semibold ${theme.text.primary} mb-1`}>Expiry Buckets</h2>
          <p className={`text-xs ${theme.text.secondary} mb-4`}>
            Distribution by days remaining to contract expiry.
          </p>
          <div className="space-y-3">
            {Object.entries(analytics.expiryBuckets).map(([label, count]) => {
              const total = Math.max(analytics.totalContracts, 1);
              const percent = Math.round((count / total) * 100);
              return (
                <div key={label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm ${theme.text.primary} flex items-center gap-2`}>
                      <span className={cn('w-2.5 h-2.5 rounded-full', dash.dot(label === 'Expired' ? 'danger' : 'info'))} />
                      {label}
                    </span>
                    <span className={`text-sm font-medium ${theme.text.secondary}`}>{count}</span>
                  </div>
                  <div className={cn('w-full h-2 rounded-full', dash.progressTrack)}>
                    <div
                      className={cn('h-2 rounded-full', dash.fill(label === 'Expired' ? 'danger' : 'info'))}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={dash.widgetShell()}>
          <h2 className={`text-base font-semibold ${theme.text.primary} mb-1`}>Top At-Risk Suppliers</h2>
          <p className={`text-xs ${theme.text.secondary} mb-4`}>
            Suppliers with the highest number of expired or soon-to-expire contracts.
          </p>
          {analytics.topRiskSuppliers.length === 0 ? (
            <p className={`text-sm ${theme.text.secondary}`}>No suppliers with expiring/expired contracts.</p>
          ) : (
            <div className="space-y-3">
              {analytics.topRiskSuppliers.map((supplier, index) => (
                <div key={supplier.name} className={dash.listItem('px-3 py-2')}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${theme.text.primary} truncate pr-2`}>
                      {index + 1}. {supplier.name}
                    </span>
                    <span className={dash.badge('warning')}>
                      {supplier.count} at risk
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={dash.widgetShell()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-base font-semibold ${theme.text.primary}`}>Contracts Needing Action</h2>
          <span className={dash.badge('neutral')}>
            {isLoading ? 'Loading...' : `${analytics.actionable.length} items`}
          </span>
        </div>
        {analytics.actionable.length === 0 ? (
          <p className={`text-sm ${theme.text.secondary}`}>No expiring/expired contracts at the moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`${theme.text.secondary} border-b ${theme.border.light}`}>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Number</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Title</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Entity</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Category</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Expiry</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Days Left</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Status</th>
                  <th className="text-left py-2.5 pr-3 text-xs uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody>
                {analytics.actionable.slice(0, 15).map((item) => (
                  <tr key={item.id} className={`border-b ${theme.border.light} hover:bg-black/5 dark:hover:bg-white/5 transition-colors`}>
                    <td className="py-2.5 pr-3 font-medium">{item.contractNumber}</td>
                    <td className="py-2.5 pr-3">{item.contractTitle}</td>
                    <td className="py-2.5 pr-3">{item.supplierName}</td>
                    <td className="py-2.5 pr-3">{item.categoryName}</td>
                    <td className="py-2.5 pr-3">
                      {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={dash.badge(
                        item.daysLeft != null && item.daysLeft < 0
                          ? 'danger'
                          : item.daysLeft != null && item.daysLeft <= 7
                          ? 'warning'
                          : 'neutral'
                      )}>
                        {item.daysLeft == null ? '—' : item.daysLeft}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={cn('inline-flex items-center gap-1.5', dash.statusBadge(item.status))}>
                        <span className={cn('w-2 h-2 rounded-full', dash.dotForStatus(item.status))} />
                        {item.status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <button
                        type="button"
                        onClick={() =>
                          navigate('/contract', {
                            state: { editContractId: item.id },
                          })
                        }
                        className={dash.linkChip()}
                        title={`Open contract ${item.contractNumber} for editing`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDashboardPage;
